// src/components/VaccineReservationList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  VStack,
  HStack,
  Spinner,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  useBreakpointValue,
  Flex,
  Tooltip,
  IconButton,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSearch,
  faCircle,
} from "@fortawesome/free-solid-svg-icons";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../themes/TableStyles"; // นำ StatusBadge ออก เพราะเราจะสร้างใหม่
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButtonSet from "../themes/IconButtonSet";
import { StandardHeader } from "../themes/StandardHeader";

const statusConfig = {
  ใบจองใหม่: { color: "blue.500", label: "ใบจองใหม่" },
  ยืนยันการจอง: { color: "green.500", label: "ยืนยันการจอง" },
  รอการอนุมัติ: { color: "orange.500", label: "รอการอนุมัติ" },
  พร้อมรับวัคซีน: { color: "pink.500", label: "พร้อมรับวัคซีน" },
  ยกเลิก: { color: "red.500", label: "ยกเลิก" },
  จัดส่งแล้ว: { color: "purple.500", label: "จัดส่งแล้ว" },
  // เพิ่มสถานะอื่นๆ ตามต้องการ
};
// NEW: สร้าง Component สำหรับแสดงสถานะแบบ "จุดสี" พร้อม Tooltip
const StatusIcon = ({ status }) => {
  const config = statusConfig[status] || { color: "gray.500", label: status };

  return (
    <Tooltip label={config.label} placement="top" hasArrow>
      <Box color={config.color}>
        <FontAwesomeIcon icon={faCircle} />
      </Box>
    </Tooltip>
  );
};

// NEW: สร้าง Component สำหรับแสดงสถานะบน Desktop (จุดสี + ข้อความ)
const StatusBadgeWithDot = ({ status }) => {
  const config = statusConfig[status] || { color: "gray.500" };

  return (
    <HStack spacing={2} align="center">
      {/* FIX: กำหนดสีที่ Box แทน */}
      <Box color={config.color} display="flex" alignItems="center">
        <FontAwesomeIcon icon={faCircle} size="xs" />
      </Box>
      <Text>{status}</Text>
    </HStack>
  );
};

// NEW: สร้าง Component สำหรับแสดงผลแบบ Card List บน Mobile
const MobileReservationList = ({ data, pickupPoints, navigate }) => {
  if (data.length === 0) {
    return (
      <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
        ไม่พบข้อมูลที่ค้นหา
      </Text>
    );
  }

  return (
    <VStack spacing={4} w="100%">
      {data.map((row) => {
        const point = pickupPoints.find(
          (p) => p.WarehouseID === row.PickupPoint
        );
        const pickupPointName = point
          ? point.WarehouseName
          : row.PickupPoint || "-";

        return (
          <Box
            key={row.ReservationID}
            w="100%"
            p={4}
            borderWidth={1}
            borderRadius="lg"
            boxShadow="sm"
          >
            <VStack align="start" spacing={3}>
              {/* --- บรรทัดแรก: เลขที่, สถานะ, ปุ่ม --- */}
              <Flex w="100%" justify="space-between" align="center">
                <Text fontWeight="bold" fontSize="md">
                  {row.ReservationID}
                </Text>
                <HStack spacing={4}>
                  <StatusBadgeWithDot status={row.ReservationStatus} />
                  <Tooltip label="ดูรายละเอียด" placement="top" hasArrow>
                    <IconButton
                      icon={<FontAwesomeIcon icon={faFileLines} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      aria-label="ดูรายละเอียด"
                      onClick={() =>
                        navigate(`/vaccinereservation/${row.ReservationID}`)
                      }
                    />
                  </Tooltip>
                </HStack>
              </Flex>

              {/* --- บรรทัดที่สอง: ข้อมูลเพิ่มเติม --- */}
              <Box w="100%" pt={2} borderTopWidth="1px" borderColor="gray.200">
                <Grid
                  templateColumns="auto 1fr"
                  gapX={2}
                  gapY={1}
                  fontSize="sm"
                  color="gray.600"
                >
                  <GridItem fontWeight="medium">จุดจำหน่าย:</GridItem>
                  <GridItem>{pickupPointName}</GridItem>

                  <GridItem fontWeight="medium">รอบจัดส่ง:</GridItem>
                  <GridItem>{row.DeliveryPeriod || "-"}</GridItem>
                </Grid>
              </Box>
            </VStack>
          </Box>
        );
      })}
    </VStack>
  );
};

const VaccineReservationList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  // Columns สำหรับ Desktop เท่านั้น
  const columns = [
    {
      name: "เลขที่ใบจอง",
      selector: (row) => row.ReservationID,
      sortable: true,
      width: "150px",
    },
    {
      name: "จุดจำหน่าย",
      selector: (row) => {
        const point = pickupPoints.find(
          (p) => p.WarehouseID === row.PickupPoint
        );
        return point ? point.WarehouseName : row.PickupPoint || "-";
      },
      sortable: true,
    },
    {
      name: "รอบการจัดส่ง",
      selector: (row) => row.DeliveryPeriod || "-",
      sortable: true,
    },
    {
      name: "วันที่จอง",
      selector: (row) => {
        let dateString = row.ReservationDate;
        if (typeof dateString === "string" && dateString.endsWith("Z")) {
          dateString = dateString.slice(0, -1);
        }
        const date = new Date(dateString);
        return date.toLocaleString("th-TH", { hour12: false });
      },
      sortable: true,
      width: "200px",
    },
    {
      name: "สถานะ",
      cell: (row) => <StatusBadgeWithDot status={row.ReservationStatus} />, // ใช้ Component ใหม่
      sortable: true,
      width: "200px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButtonSet.View
          icon={faFileLines}
          onClick={() => navigate(`/vaccinereservation/${row.ReservationID}`)}
          label="รายละเอียด" // บน Desktop ยังคงมี Label
        />
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchReservations = async () => {
    setIsLoading(true);
    try {
      await fetchPickupPoints();
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationsList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineReservationsList || [];
        setReservations(data);
        setFilteredReservations(data);
      }
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchReservations();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    if (!value) {
      setFilteredReservations(reservations);
      return;
    }

    const searchTerms = value.split(" ").filter((term) => term.length > 0);
    const filtered = reservations.filter((row) => {
      const pickupPointName =
        pickupPoints.find((p) => p.WarehouseID === row.PickupPoint)
          ?.WarehouseName ||
        row.PickupPoint ||
        "";

      return searchTerms.every((term) =>
        [
          row.ReservationID.toLowerCase(),
          row.FarmerName ? row.FarmerName.toLowerCase() : "",
          row.DeliveryPeriod ? row.DeliveryPeriod.toLowerCase() : "",
          pickupPointName.toLowerCase(),
          row.ReservationStatus.toLowerCase(),
        ].some((field) => field.includes(term))
      );
    });
    setFilteredReservations(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilteredReservations(reservations);
  };

  const handleCreateNew = () => {
    navigate("/vaccinereservation/new");
  };

  return (
    <Box
      p={{ base: 4, md: 6 }}
      m={{ base: 2, md: 4 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <StandardHeader
          title="รายการใบจองวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
        >
          <InputGroup
            w={{ base: "100%", md: "50%", lg: "30%" }}
            mb={{ base: 4, md: 0 }}
          >
            <InputLeftElement pointerEvents="none">
              <FontAwesomeIcon icon={faSearch} color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="ค้นหาใบจอง..."
              value={searchTerm}
              onChange={handleSearch}
              colorScheme="iBlue"
            />
            <IconButtonSet.Clear onClick={handleClearSearch} ml={2} />
          </InputGroup>
          <IconButtonSet.Add onClick={handleCreateNew} label="สร้างใบจองใหม่" />
        </HStack>

        {/* --- NEW: Conditional Rendering --- */}
        {isLoading ? (
          <Flex justify="center" w="100%">
            <Spinner size="xl" />
          </Flex>
        ) : reservations.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลใบจองวัคซีน
          </Text>
        ) : isMobile ? (
          <>
            <MobileReservationList
              data={filteredReservations}
              pickupPoints={pickupPoints}
              navigate={navigate}
            />
          </>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredReservations}
              pagination
              highlightOnHover
              striped
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccineReservationList;
